
import { useCallback } from 'react';

interface UseHtmlPreviewProps {
  systemVariables: Record<string, string>;
  variablesJson: string;
  variablesValid: boolean;
}

export function useHtmlPreview({
  systemVariables,
  variablesJson,
  variablesValid
}: UseHtmlPreviewProps) {
  // Process HTML to replace template variables with example values
  const getPreviewHtmlWithExampleValues = useCallback((html: string): string => {
    if (!html) return '';
    
    try {
      let processedHtml = html;
      const variables: Record<string, string> = {};
      
      // Parse custom variables if valid JSON
      if (variablesValid && variablesJson) {
        try {
          const parsedVars = JSON.parse(variablesJson);
          if (parsedVars && typeof parsedVars === 'object') {
            Object.entries(parsedVars).forEach(([key, value]) => {
              if (key) {
                variables[key] = String(value || `[Example ${key}]`);
              }
            });
          }
        } catch (err) {
          console.error('Error parsing variables JSON:', err);
        }
      }
      
      // Add system variables
      if (systemVariables && typeof systemVariables === 'object') {
        Object.entries(systemVariables).forEach(([key, value]) => {
          if (key) {
            variables[key] = String(value || '');
          }
        });
      }
      
      // Replace variables in HTML
      Object.entries(variables).forEach(([key, value]) => {
        if (key && value !== undefined) {
          const regex = new RegExp(`{{${key}}}`, 'g');
          processedHtml = processedHtml.replace(regex, String(value));
        }
      });
      
      // Handle conditionals in a simple way for preview
      const conditionalRegex = /{{#if\s+([^}]+)}}([\s\S]*?)(?:{{else}}([\s\S]*?))?{{\/if}}/g;
      processedHtml = processedHtml.replace(conditionalRegex, (match, condition, thenContent, elseContent = '') => {
        // For preview purposes, show both possibilities with styling
        return `
          <div style="border-left: 2px solid #4CAF50; padding-left: 8px; margin: 4px 0;">
            <div style="color: #4CAF50; font-size: 12px;">If ${condition} is true:</div>
            ${thenContent || ''}
          </div>
          ${elseContent ? `
          <div style="border-left: 2px solid #F44336; padding-left: 8px; margin: 4px 0;">
            <div style="color: #F44336; font-size: 12px;">If ${condition} is false:</div>
            ${elseContent}
          </div>` : ''}
        `;
      });
      
      return processedHtml;
    } catch (err: any) {
      console.error('Error processing HTML preview:', err);
      return `<div style="color: red; padding: 20px;">Error generating preview: ${err.message}</div>`;
    }
  }, [systemVariables, variablesJson, variablesValid]);
  
  return { getPreviewHtmlWithExampleValues };
}
